import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import { usePayUTokenize } from '../PayUTokenizeContext';
import styles from './PayUSecureForm.styl';

const cx = classNames.bind(styles);

const PAYU_SDK_SRC = 'https://secure.payu.com/javascript/sdk';

// const PAYU_SDK_SRC = 'https://secure.snd.payu.com/javascript/sdk';

function loadPayUSdk() {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${PAYU_SDK_SRC}"]`);
    if (existing) {
      if (typeof window.PayU === 'function') return resolve();
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', () => reject(new Error('PayU SDK load error')));
      return;
    }

    const script = document.createElement('script');
    script.src = PAYU_SDK_SRC;
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error('PayU SDK load error'));
    document.body.appendChild(script);
  });
}

function isNumericPosId(posId) {
  return /^\d+$/.test(String(posId));
}

export default function PayUSecureForm({
  posId,
  lang = 'pl',
  dev = true,
  tokenizeType = 'SINGLE', // 'SINGLE' | 'MULTI'
  onToken,
  onError, // optional
  buttonText, // optional
  hideTokenizeButton = false,
  layout = 'desktop', // 'desktop' | 'mobile'
}) {
  const tokenizeCtx = usePayUTokenize();
  const idBase = useRef(`payu-${Math.random().toString(16).slice(2)}`).current;
  const fieldIds = useRef({
    number: `${idBase}-number`,
    date: `${idBase}-date`,
    cvv: `${idBase}-cvv`,
  });
  const secureFormsRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setError('');
        setReady(false);

        if (!posId) throw new Error('posId is required');
        if (!isNumericPosId(posId))
          throw new Error(`posId must be numeric, got "${String(posId)}"`);

        await loadPayUSdk();
        if (!mounted) return;

        if (typeof window.PayU !== 'function') {
          throw new Error('PayU SDK loaded, but window.PayU is not available');
        }

        const payu = window.PayU(String(posId), { lang, dev });

        const formOptions = {
          style: {
            basic: { fontSize: '18px', fontWeight: '500' },
          },
        };
        const secureForms = payu.secureForms({ lang });

        secureFormsRef.current = secureForms;

        const renderForm = () => {
          if (!mounted) return;
          const ids = fieldIds.current;
          const numEl = document.getElementById(ids.number);
          const dateEl = document.getElementById(ids.date);
          const cvvEl = document.getElementById(ids.cvv);
          if (numEl && dateEl && cvvEl) {
            secureForms.add('number', formOptions).render(`#${ids.number}`);
            secureForms.add('date', formOptions).render(`#${ids.date}`);
            secureForms.add('cvv', formOptions).render(`#${ids.cvv}`);
          }
          if (mounted) setReady(true);
        };
        requestAnimationFrame(() => {
          setTimeout(renderForm, 150);
        });
      } catch (e) {
        const msg = e?.message || String(e);
        setError(msg);
        onError?.(e);
      }
    })();

    return () => {
      mounted = false;
      ['number', 'date', 'cvv'].forEach(key => {
        const el = document.getElementById(fieldIds.current[key]);
        if (el) el.innerHTML = '';
      });
      secureFormsRef.current = null;
    };
  }, [posId, lang, dev]);

  const tokenize = async () => {
    setBusy(true);
    setError('');

    try {
      const secureForms = secureFormsRef.current;
      if (!secureForms) throw new Error('Secure Form is not initialized');

      if (!isNumericPosId(posId)) throw new Error(`posId must be numeric, got "${String(posId)}"`);

      const numericPosId = String(posId);

      let resp;
      try {
        resp = await secureForms.tokenize(numericPosId, tokenizeType);
      } catch (_) {
        resp = await secureForms.tokenize(numericPosId);
      }

      if (resp?.status === 'ERROR') {
        const corr = resp?.correlationId;
        const msg = resp?.error?.messages?.[0]?.message || 'Tokenization error';
        throw new Error(corr ? `${msg} (corrId=${corr})` : msg);
      }
      const token =
        resp?.value || resp?.token || resp?.data?.token || resp?.body?.token || resp?.result?.token;

      if (!token) throw new Error('Token not found in response');

      onToken?.(token, resp);
      return token;
    } catch (e) {
      const msg = e?.message || String(e);
      setError(msg);
      onError?.(e);
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const tokenizeRef = useRef(tokenize);
  tokenizeRef.current = tokenize;
  useEffect(() => {
    if (tokenizeCtx && hideTokenizeButton) {
      tokenizeCtx.registerTokenize(() => tokenizeRef.current());
      return () => tokenizeCtx.unregisterTokenize();
    }
    return undefined;
  }, [tokenizeCtx, hideTokenizeButton]);

  const isMobile = layout === 'mobile';

  return (
    <div className={cx('PayUSecureForm', { 'PayUSecureForm_mobile': isMobile })}>
      <div className={cx('PayUSecureForm__wrapper')}>
        <div
          id={fieldIds.current.number}
          className={cx('PayUSecureForm__field', 'PayUSecureForm__field_number')}
        />
        <div className={cx('PayUSecureForm__row')}>
          <div
            id={fieldIds.current.date}
            className={cx('PayUSecureForm__field', 'PayUSecureForm__field_date')}
          />
          <div
            id={fieldIds.current.cvv}
            className={cx('PayUSecureForm__field', 'PayUSecureForm__field_cvv')}
          />
        </div>
      </div>

      {!hideTokenizeButton && (
        <div style={{ marginTop: 12 }}>
          <button type="button" disabled={!ready || busy} onClick={tokenize}>
            {busy ? 'Processing...' : buttonText || `Tokenize (${tokenizeType})`}
          </button>
        </div>
      )}

      {error ? <div style={{ marginTop: 10, color: '#b00020' }}>{error}</div> : null}
    </div>
  );
}
